import { useContext, useEffect, useState } from 'react';
import { isNilOrEmpty } from '../../../../utils/objectUtils';
import {
    useLockFixedAssetAssetEntriesMutation,
    useUnlockFixedAssetAssetEntriesMutation,
} from '../../../../services/fixedAssets/fixedAssets.service';
import { useDispatch } from 'react-redux';
import { AssetEntryActionsProps } from '../AssetEntryActionsModal';
import { AlertColor } from '@mui/material';
import { openSnackbarBasicWithMessage } from '../../../../store/uiElements';
import SettingsContext from '../../../../contexts/settings.context';
import { useGetTermSetQuery } from '../../../../services/i18n/i18n.service';
import { ACCOUNTING_DEFS } from '../../../../constants/i18n/translations/termSetDefinitions/accounting';
import { skipToken } from '@reduxjs/toolkit/query';

const useAssetEntryActionsModal = (props: AssetEntryActionsProps) => {
    const dispatch = useDispatch();
    const { settings } = useContext(SettingsContext);
    const { data: termSet } = useGetTermSetQuery(
        settings?.userSettings
            ? {
                  languageId: settings?.userSettings?.languageId,
                  code: ACCOUNTING_DEFS.ASSET_ENTRIES_GRID,
              }
            : skipToken
    );
    const [shouldLockEntries, setShouldLockEntries] = useState<boolean>(false);
    const [canToggleLockUnlock, setCanToggleLockUnlock] =
        useState<boolean>(false);

    const [lockEntries, { isLoading: isLockingEntries }] =
        useLockFixedAssetAssetEntriesMutation();
    const [unlockEntries, { isLoading: isUnlockingEntries }] =
        useUnlockFixedAssetAssetEntriesMutation();

    useEffect(() => {
        if (!isNilOrEmpty(props.selected)) {
            const hasUnlockedEntries: boolean = props.selected.some(
                (entry) => !entry.locked
            );
            const hasLockedEntries: boolean = props.selected.some(
                (entry) => entry.locked
            );

            setShouldLockEntries(hasUnlockedEntries);
            setCanToggleLockUnlock(hasUnlockedEntries && hasLockedEntries);
        } else {
            setShouldLockEntries(false);
            setCanToggleLockUnlock(false);
        }
    }, [props.selected]);

    const handleUpdateEntries = async (event: any) => {
        event.preventDefault();

        if (shouldLockEntries) {
            const idList: bigint[] = props.selected
                .filter((entry) => !entry.locked)
                .map((entry) => entry.id);

            try {
                await lockEntries({ ids: idList });
            } catch (e) {
                _showNotification(e.message, 'error');
            }
        } else {
            const idList: bigint[] = props.selected
                .filter((entry) => entry.locked)
                .map((entry) => entry.id);

            try {
                await unlockEntries({ ids: idList });
            } catch (e) {
                _showNotification(e.message, 'error');
            }
        }

        props.onClose();
    };

    const _showNotification = (message: string, severity: AlertColor) => {
        dispatch(
            openSnackbarBasicWithMessage({
                message: message,
                severity: severity,
            })
        );
    };

    return {
        shouldLockEntries,
        setShouldLockEntries,
        canToggleLockUnlock,
        isLockingEntries,
        isUnlockingEntries,
        handleUpdateEntries,
        termSet,
    };
};

export default useAssetEntryActionsModal;
